import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Coins, CheckCircle, AlertCircle, Loader2, Copy, ExternalLink } from 'lucide-react';
import { useWeb3Wallet } from '../contexts/RainbowKitProvider';
import { useTransaction } from '../contexts/TransactionContext';
import ethereumService from '../services/ethereumService';
import { ethers } from 'ethers';

interface AirdropStats {
  totalClaimed: string;
  remainingSupply: string;
  userClaimed: boolean;
  gasFee: string;
}

const AirdropPage: React.FC = () => {
  const { isConnected, address } = useWeb3Wallet();
  const { addTransaction } = useTransaction();
  const [claimCode, setClaimCode] = useState('');
  const [selectedTier, setSelectedTier] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [airdropStats, setAirdropStats] = useState<AirdropStats | null>(null);
  const [txHash, setTxHash] = useState('');

  // Airdrop tiers
  const airdropTiers = [
    { id: 0, name: 'Bronze', amount: '1,000', color: 'from-orange-400 to-orange-600', emoji: '🥉' },
    { id: 1, name: 'Silver', amount: '2,500', color: 'from-gray-400 to-gray-600', emoji: '🥈' },
    { id: 2, name: 'Gold', amount: '5,000', color: 'from-yellow-400 to-yellow-600', emoji: '🥇' },
    { id: 3, name: 'Platinum', amount: '10,000', color: 'from-purple-400 to-purple-600', emoji: '💎' },
    { id: 4, name: 'Diamond', amount: '25,000', color: 'from-blue-400 to-blue-600', emoji: '💠' }
  ];

  useEffect(() => {
    if (isConnected && address) {
      loadAirdropStats();
    }
  }, [isConnected, address]);

  const loadAirdropStats = async () => {
    try {
      const stats = await ethereumService.getVoiceAirdropStats();
      const hasClaimed = await ethereumService.hasClaimedAirdrop(address!);
      const gasFee = await ethereumService.getVoiceGasFee();
      
      setAirdropStats({
        totalClaimed: stats.totalClaimed,
        remainingSupply: stats.remainingSupply,
        userClaimed: hasClaimed,
        gasFee: ethers.formatEther(gasFee)
      });
    } catch (error) {
      console.error('Failed to load airdrop stats:', error);
    }
  };

  const handleClaimAirdrop = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet first');
      return;
    }

    if (!claimCode.trim()) {
      setError('Please enter a valid claim code');
      return;
    }

    if (airdropStats?.userClaimed) {
      setError('You have already claimed your airdrop');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // First, get claim data from Telegram bot API
      console.log('Fetching claim data from Telegram bot...');
      const botUrl = import.meta.env.VITE_TELEGRAM_BOT_URL || 'https://voice-bot.criskan92.workers.dev';
      const response = await fetch(`${botUrl}/api/get-claim-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          claimCode: claimCode.trim(),
          userAddress: address
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 404) {
          throw new Error('Invalid claim code. Please get a valid claim code from our Telegram bot @VoicePayBot first.');
        } else if (response.status === 400 && errorData.error?.includes('not activated')) {
          throw new Error('Claim code not activated. Please use /claim command in Telegram bot first.');
        }
        throw new Error(errorData.error || 'Failed to verify claim code');
      }

      const claimData = await response.json();
      console.log('Claim data received:', {
        tier: claimData.tier,
        tierName: claimData.tierName,
        amount: claimData.amount,
        signature: claimData.signature.substring(0, 10) + '...'
      });

      // Now claim airdrop with the signature from Telegram bot
      const result = await ethereumService.claimVoiceAirdrop(
        claimCode.trim(),
        claimData.tier,
        claimData.signature
      );

      if (result.success) {
        setTxHash(result.hash);
        setShowSuccess(true);
        
        // Add transaction to history
        addTransaction({
          type: 'airdrop_claim',
          blockchain: 'ethereum',
          amount: claimData.amount,
          token: 'VOICE',
          status: 'completed',
          hash: result.hash
        });

        // Reload stats
        await loadAirdropStats();
      } else {
        setError(result.error || 'Failed to claim airdrop');
      }
    } catch (error) {
      console.error('Airdrop claim error:', error);
      let errorMessage = 'Failed to claim airdrop';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('fetch')) {
          errorMessage = 'Failed to claim airdrop. Unable to connect to claim service. Please check your internet connection and try again.';
        } else if (error.message.includes('Invalid claim code')) {
          errorMessage = 'Invalid claim code. Please check your code from Telegram bot.';
        } else if (error.message.includes('Claim code not activated')) {
          errorMessage = 'Claim code not activated. Please use /claim command in Telegram bot first.';
        } else if (error.message.includes('Invalid Ethereum address')) {
          errorMessage = 'Invalid wallet address format.';
        } else if (error.message.includes('already claimed')) {
          errorMessage = 'You have already claimed your airdrop.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const openBSCScan = (hash: string) => {
    window.open(`https://bscscan.com/tx/${hash}`, '_blank');
  };

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center space-x-2">
          <Gift className="w-8 h-8 text-yellow-400" />
          <h1 className="text-2xl font-bold text-white">VOICE Airdrop</h1>
        </div>
        <p className="text-white/60 text-sm">
          Claim your free VOICE tokens with a valid claim code
        </p>
      </div>

      {/* How to Get Claim Code */}
      <div className="bg-blue-500/10 backdrop-blur-sm rounded-2xl p-4 border border-blue-500/20">
        <h3 className="text-blue-400 font-semibold mb-2 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          How to Get Your Claim Code
        </h3>
        <div className="text-white/80 text-sm space-y-2">
          <p>1. Join our Telegram bot: <a href="https://t.me/VoicePayBot" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">@VoicePayBot</a></p>
          <p>2. Use the <code className="bg-black/30 px-2 py-1 rounded text-blue-300">/claim</code> command to get your unique claim code</p>
          <p>3. Copy the claim code and paste it below to claim your tokens</p>
        </div>
      </div>

      {/* Airdrop Stats */}
      {airdropStats && (
        <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
          <h3 className="text-white font-semibold mb-3 flex items-center">
            <Coins className="w-5 h-5 mr-2 text-yellow-400" />
            Airdrop Statistics
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-white/60 text-xs">Total Claimed</p>
              <p className="text-white font-bold">{airdropStats.totalClaimed} VOICE</p>
            </div>
            <div className="text-center">
              <p className="text-white/60 text-xs">Remaining Supply</p>
              <p className="text-white font-bold">{airdropStats.remainingSupply} VOICE</p>
            </div>
            <div className="text-center">
              <p className="text-white/60 text-xs">Gas Fee</p>
              <p className="text-white font-bold">{airdropStats.gasFee} BNB</p>
            </div>
            <div className="text-center">
              <p className="text-white/60 text-xs">Your Status</p>
              <p className={`font-bold ${
                airdropStats.userClaimed ? 'text-green-400' : 'text-yellow-400'
              }`}>
                {airdropStats.userClaimed ? 'Claimed' : 'Available'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tier Selection */}
      <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
        <h3 className="text-white font-semibold mb-3">Select Airdrop Tier</h3>
        <div className="grid grid-cols-1 gap-2">
          {airdropTiers.map((tier) => (
            <motion.button
              key={tier.id}
              onClick={() => setSelectedTier(tier.id)}
              className={`p-3 rounded-xl border transition-all ${
                selectedTier === tier.id
                  ? 'border-yellow-400 bg-yellow-400/10'
                  : 'border-white/10 bg-white/5 hover:bg-white/10'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{tier.emoji}</span>
                  <div className="text-left">
                    <p className="text-white font-semibold">{tier.name}</p>
                    <p className="text-white/60 text-sm">{tier.amount} VOICE</p>
                  </div>
                </div>
                {selectedTier === tier.id && (
                  <CheckCircle className="w-5 h-5 text-yellow-400" />
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Claim Code Input */}
      <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
        <h3 className="text-white font-semibold mb-3">Enter Claim Code</h3>
        <div className="space-y-3">
          <input
            type="text"
            value={claimCode}
            onChange={(e) => setClaimCode(e.target.value)}
            placeholder="Enter your claim code here..."
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400"
            disabled={isLoading || airdropStats?.userClaimed}
          />
          
          {error && (
            <div className="flex items-center space-x-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}

          <p className="text-white/60 text-xs">
            💡 Get your claim code from our Telegram bot: @Voice_agents
          </p>
        </div>
      </div>

      {/* Claim Button */}
      {isConnected ? (
        <motion.button
          onClick={handleClaimAirdrop}
          disabled={isLoading || !claimCode.trim() || airdropStats?.userClaimed}
          className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all ${
            isLoading || !claimCode.trim() || airdropStats?.userClaimed
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-500 hover:to-orange-600'
          }`}
          whileHover={!isLoading && claimCode.trim() && !airdropStats?.userClaimed ? { scale: 1.02 } : {}}
          whileTap={!isLoading && claimCode.trim() && !airdropStats?.userClaimed ? { scale: 0.98 } : {}}
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Claiming Airdrop...</span>
            </div>
          ) : airdropStats?.userClaimed ? (
            'Already Claimed'
          ) : (
            `Claim ${airdropTiers[selectedTier].amount} VOICE Tokens`
          )}
        </motion.button>
      ) : (
        <div className="text-center py-8">
          <p className="text-white/60 mb-4">Connect your wallet to claim airdrop</p>
          <button className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all">
            Connect Wallet
          </button>
        </div>
      )}

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowSuccess(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 backdrop-blur-sm rounded-2xl p-6 border border-white/10 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Airdrop Claimed!</h3>
                  <p className="text-white/60 text-sm mb-4">
                    You have successfully claimed {airdropTiers[selectedTier].amount} VOICE tokens
                  </p>
                </div>

                {txHash && (
                  <div className="space-y-2">
                    <p className="text-white/60 text-xs">Transaction Hash:</p>
                    <div className="flex items-center space-x-2 bg-white/10 rounded-lg p-2">
                      <span className="text-white text-xs font-mono truncate flex-1">
                        {txHash.slice(0, 10)}...{txHash.slice(-8)}
                      </span>
                      <button
                        onClick={() => copyToClipboard(txHash)}
                        className="p-1 hover:bg-white/10 rounded"
                      >
                        <Copy className="w-4 h-4 text-white/60" />
                      </button>
                      <button
                        onClick={() => openBSCScan(txHash)}
                        className="p-1 hover:bg-white/10 rounded"
                      >
                        <ExternalLink className="w-4 h-4 text-white/60" />
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setShowSuccess(false)}
                  className="w-full py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-xl font-semibold hover:from-yellow-500 hover:to-orange-600 transition-all"
                >
                  Continue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AirdropPage;